import { useState } from 'react';
import useTable from '@/hooks/useTable';
import { ErrorBlock, LoadingBlock } from '@/components/feature/SectionState';
import SponsorSlot from '@/components/feature/SponsorSlot';
import VideoPreviewModal from '@/components/feature/VideoPreviewModal';
import type { GsVideo } from '@/lib/types';

export default function Watch() {
  const { data: videos, loading, error, reload } = useTable<GsVideo>('gs_videos');
  const [active, setActive] = useState<GsVideo | null>(null);

  return (
    <section id="watch" className="bg-foreground-950 px-4 md:px-8 py-16 md:py-24">
      <div className="flex items-center gap-3">
        <span className="w-2.5 h-2.5 rounded-full bg-accent-500 animate-pulse"></span>
        <p className="font-label uppercase tracking-[0.28em] text-xs text-accent-400 font-semibold">Watch</p>
      </div>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mt-3 mb-10">
        <div>
          <h2 className="font-heading font-bold text-4xl md:text-6xl text-background-50 leading-[1.02]">Video-first coverage</h2>
          <p className="text-foreground-300 mt-3 max-w-xl text-base">The Watch series — a premium sponsorship a brand can own end to end.</p>
        </div>
        <SponsorSlot tone="dark" label="Watch series presented by" />
      </div>

      {loading && <LoadingBlock label="Loading videos" />}
      {error && <ErrorBlock message={error} onRetry={reload} />}

      {!loading && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {videos.map((v) => (
            <button key={v.id} type="button" onClick={() => setActive(v)} aria-label={`Play ${v.title}`} className="group cursor-pointer text-left w-full">
              <div className="relative w-full h-56 rounded-lg overflow-hidden">
                <img src={v.image} alt={v.title} title={`${v.title} video`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-foreground-950/30 group-hover:bg-foreground-950/10 transition-colors"></div>
                <span className="absolute inset-0 m-auto w-16 h-16 rounded-full bg-accent-500 text-foreground-950 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-play-fill text-3xl"></i>
                </span>
              </div>
              <h3 className="mt-4 font-heading font-bold text-2xl text-background-50 group-hover:text-accent-400">{v.title}</h3>
              <p className="text-sm text-foreground-300 mt-1">{v.body}</p>
            </button>
          ))}
        </div>
      )}

      <VideoPreviewModal video={active} onClose={() => setActive(null)} />
    </section>
  );
}